import { css } from 'aphrodite';
import * as React from 'react';
import { useCoachingConversationCallView } from '../../../queries/AIConversation/useCoachingConversationCallView';
import { Button } from '../../../web/components-v2/Button';
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '../../../web/components-v2/Collapsible';
import { ChevronDown } from '../../../web/components/svgs/icons/ChevronDown';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { CoachingAdherenceScorecard } from '../../components/CallRecordingDetails/components/CoachingAdherenceScorecard';
import { useManagerPermissions } from '../../hooks/useManagerPermissions';
import { styleSheet } from './adherenceDrawer.styles';
import { CoachQualityTile } from './CoachingAdherence';
import { ConversationInsights } from './ConversationInsights';
import { MetadataPanel } from './MetadataPanel';
import { RubricBreakdown } from './RubricBreakdown';
import { styleSheet as pageStyleSheet } from './styles';

const CloseIcon = () => (
	<svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' aria-hidden='true'>
		<path d='M6 6l12 12M18 6L6 18' />
	</svg>
);

interface IProps {
	conversationId: string | null;
	onClose?: () => void;
}

export function CallScoringDrawerContent({ conversationId, onClose }: IProps) {
	return (
		<div className={css(styleSheet.root)}>
			<header className={css(styleSheet.drawerHeader)}>
				<h2 className={css(styleSheet.drawerTitle)}>Call scoring &amp; details</h2>
				{onClose && (
					<Button
						variant='ghost'
						size='icon'
						onClick={onClose}
						aria-label='Close call scoring and details'
						styles={[styleSheet.closeButton]}
					>
						<CloseIcon />
					</Button>
				)}
			</header>
			{conversationId ? (
				<>
					<CallScoringSection conversationId={conversationId} />
					<MetadataPanel conversationId={conversationId} />
					<ConversationInsights conversationId={conversationId} />
				</>
			) : (
				<div className={css(styleSheet.stateWrap)}>Select a conversation to see its scoring and details.</div>
			)}
		</div>
	);
}

/**
 * Rubric scoring, collapsed: managers review adherence in the middle panel, so this stays out of the way.
 * New records (`rubricBreakdown` present) render {@link RubricBreakdown}; older ones the original
 * {@link CoachingAdherenceScorecard}.
 */
function CallScoringSection({ conversationId }: { conversationId: string }) {
	const [isOpen, setIsOpen] = React.useState(false);
	const { hasManagerPermissions, isLoading: permissionsLoading } = useManagerPermissions();
	const { callView, isLoading } = useCoachingConversationCallView(conversationId);

	if (permissionsLoading || !hasManagerPermissions) {
		return null;
	}

	const phoneCall = callView?.phoneCall ?? null;
	const result = phoneCall?.coachingAdherenceResult ?? null;

	const renderBody = () => {
		if (isLoading) {
			return (
				<div className={css(styleSheet.stateSpinner)}>
					<LoadingSpinner type='small' />
				</div>
			);
		}
		if (!phoneCall || !result) {
			return <div className={css(styleSheet.stateWrap)}>No call scoring found for this conversation.</div>;
		}
		if (result.rubricBreakdown) {
			return <RubricBreakdown result={result} />;
		}
		return (
			<div className={css(styleSheet.legacyWrap)}>
				<CoachingAdherenceScorecard phoneCall={phoneCall} />
				<CoachQualityTile result={result} />
			</div>
		);
	};

	return (
		<Collapsible open={isOpen} onOpenChange={setIsOpen} className={css(pageStyleSheet.metadata)}>
			<div className={css(pageStyleSheet.metadataHeader)}>
				<h3 className={css(pageStyleSheet.metadataTitle)}>Call scoring</h3>
				<CollapsibleTrigger render={<Button variant='ghost' size='icon' aria-label={isOpen ? 'Collapse' : 'Expand'} />}>
					<ChevronDown
						className={css(pageStyleSheet.collapseChevron, isOpen ? pageStyleSheet.collapseChevronOpen : null)}
						width={18}
						height={18}
					/>
				</CollapsibleTrigger>
			</div>
			<CollapsiblePanel>{renderBody()}</CollapsiblePanel>
		</Collapsible>
	);
}
